/* Courier PoD reminders: the daily 10:00 mail (on/off switch, days) and a manual Send now / Send test to me.
   Each courier gets one mail listing its shipments delivered over the on-time window ago (last N days) with no PoD.
   Backend: /api/pod/mails/courier-reminders (preview), /send, /auto. */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Box, Button, Card, CardContent, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  LinearProgress, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { useSnackbar } from 'notistack'
import { get, post, put } from '../../common/constants'
import { tableHeadSx } from '../../common/sharedStyles'
import { getUserName } from '../../helpers/ReduxHelpers'
import { TONE, errText, fmtDateTime, num } from './podUtils'

const MAIL_BASE = 'pod/mails/courier-reminders'
const DAY_SHORT = { MONDAY: 'Mon', TUESDAY: 'Tue', WEDNESDAY: 'Wed', THURSDAY: 'Thu', FRIDAY: 'Fri', SATURDAY: 'Sat', SUNDAY: 'Sun' }
const ORDER = Object.keys(DAY_SHORT)

/** ['MONDAY'…'SATURDAY'] → "Mon–Sat"; other sets → "Mon, Wed, Fri". */
export const daysText = (days = []) => {
  const idx = ORDER.map((d) => days.includes(d))
  if (!days.length) return 'no days'
  const first = idx.indexOf(true); const last = idx.lastIndexOf(true)
  const run = idx.slice(first, last + 1).every(Boolean)
  return run && last - first >= 2 ? `${DAY_SHORT[ORDER[first]]}–${DAY_SHORT[ORDER[last]]}` : ORDER.filter((d) => days.includes(d)).map((d) => DAY_SHORT[d]).join(', ')
}

const STATUS = { SENT: { label: 'Sent', tone: 'success' }, SKIPPED: { label: 'Skipped', tone: 'neutral' }, FAILED: { label: 'Failed', tone: 'error' } }
const toneChipSx = (tone) => ({ bgcolor: TONE[tone].bg, color: TONE[tone].fg, border: 1, borderColor: TONE[tone].border, fontWeight: 600,
  '& .MuiChip-label': { textTransform: 'none' } })

function PodRemindersCard({ embedded, onStatus }, ref) {
  const { enqueueSnackbar } = useSnackbar()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selected, setSelected] = useState([])
  const [confirm, setConfirm] = useState(null) // { test: bool, ids: [] }
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState(null)
  const [offOpen, setOffOpen] = useState(false)
  const [offReason, setOffReason] = useState('')
  const [switching, setSwitching] = useState(false)
  const [testTo, setTestTo] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    get(MAIL_BASE)
      .then(({ data: d }) => {
        setData(d)
        setSelected(d.rows.filter((r) => r.emails?.length).map((r) => r.courierId))
      })
      .catch((e) => setError(errText(e, 'Could not load the courier reminders.')))
      .finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  const rows = useMemo(() => data?.rows || [], [data])
  // lets the page show the switch state and how many couriers are waiting on its header button
  useEffect(() => { if (data && onStatus) onStatus({ autoEnabled: !!data.autoEnabled, couriers: rows.length }) }, [data, rows, onStatus])
  const mailable = rows.filter((r) => r.emails?.length)
  const noEmail = rows.filter((r) => !r.emails?.length)

  // "Email courier" from the scorecard: select only that courier and open the confirmation.
  useImperativeHandle(ref, () => ({
    emailCourier: (courierId) => {
      if (!rows.some((r) => r.courierId === courierId)) {
        enqueueSnackbar('This courier has no PoD pending past the on-time window in the reminder period.', { variant: 'info' })
        return
      }
      setSelected([courierId])
      setConfirm({ test: false, ids: [courierId] })
    },
  }), [rows, enqueueSnackbar])

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))
  const allOn = mailable.length > 0 && mailable.every((r) => selected.includes(r.courierId))

  const testToOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testTo.trim())
  const openTest = (ids) => { setTestTo(getUserName() || ''); setConfirm({ test: true, ids }) }

  const send = async () => {
    setSending(true)
    try {
      const { data: res } = await post(`${MAIL_BASE}/send`, { courierIds: confirm.ids, test: confirm.test, testTo: confirm.test ? testTo.trim() : undefined })
      setResult(res)
      setConfirm(null)
      load()
    } catch (e) {
      enqueueSnackbar(errText(e, 'Could not send the reminders.'), { variant: 'error' })
    } finally {
      setSending(false)
    }
  }

  const setAuto = async (enabled, reason) => {
    setSwitching(true)
    try {
      const { data: d } = await put(`${MAIL_BASE}/auto`, { enabled, reason })
      setData(d)
      enqueueSnackbar(enabled ? 'Daily courier email switched on' : 'Daily courier email switched off', { variant: 'success' })
      setOffOpen(false)
      setOffReason('')
    } catch (e) {
      enqueueSnackbar(errText(e, 'Could not change the switch.'), { variant: 'error' })
    } finally {
      setSwitching(false)
    }
  }

  const confirmRows = confirm ? rows.filter((r) => confirm.ids.includes(r.courierId)) : []

  return (
    <Card elevation={0} sx={embedded ? { boxShadow: 'none' } : { border: 1, borderColor: 'divider', mb: 2 }}>
      {loading && <LinearProgress />}
      <CardContent>
        {/* header: what it does + the on/off switch */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
          <Box sx={{ maxWidth: 640 }}>
            <Typography component='h2' sx={{ fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', gap: 1 }}>
              <MailOutlineIcon fontSize='small' aria-hidden /> Courier PoD reminders
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              Each courier gets one email listing its PTL shipments delivered more than <b>{data?.afterHours ?? 24} hours</b> ago
              (last <b>{data?.lookbackDays ?? 30} days</b>) that still have no PoD, with the list attached.
              {data?.cc?.length ? <> Copied to <b>{data.cc.join(', ')}</b>.</> : null}
            </Typography>
          </Box>
          <Box sx={{ border: 1, borderColor: data?.autoEnabled ? TONE.success.border : 'divider', bgcolor: data?.autoEnabled ? TONE.success.bg : TONE.neutral.bg,
            borderRadius: 2, px: 1.5, py: 1, minWidth: 250 }}>
            <FormControlLabel
              control={<Switch checked={!!data?.autoEnabled} disabled={!data || switching}
                onChange={(e) => (e.target.checked ? setAuto(true) : setOffOpen(true))} inputProps={{ 'aria-label': 'Daily courier email' }} />}
              label={<Typography sx={{ fontWeight: 700, fontSize: 14 }}>Daily email: {data?.autoEnabled ? 'On' : 'Off'}</Typography>} />
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              {data ? `${daysText(data.days)} at ${data.sendTime} IST` : '…'}
              {data?.autoChangedBy && <><br />{data.autoEnabled ? 'Switched on' : 'Switched off'} by {data.autoChangedBy}{data.autoChangedAt ? ` · ${fmtDateTime(String(data.autoChangedAt).replace(' ', 'T'))}` : ''}</>}
            </Typography>
          </Box>
        </Box>

        {data?.testOnly && (
          <Alert severity='info' sx={{ mb: 1.5 }}>Test environment: every email goes only to you, never to the courier. The email shows who it would have gone to.</Alert>
        )}
        {error && <Alert severity='error' sx={{ mb: 1.5 }} action={<Button color='inherit' size='small' onClick={load}>Try again</Button>}>{error}</Alert>}
        {noEmail.length > 0 && (
          <Alert severity='warning' icon={<WarningAmberOutlinedIcon fontSize='inherit' />} sx={{ mb: 1.5 }}
            action={<Button component={Link} to='/courier-config/master?tab=2' color='inherit' size='small'>Courier master</Button>}>
            {noEmail.length} {noEmail.length === 1 ? 'courier has' : 'couriers have'} no email on the PTL courier master, so they cannot be reminded:{' '}
            <b>{noEmail.slice(0, 5).map((r) => r.courierName).join(', ')}</b>{noEmail.length > 5 ? ` and ${noEmail.length - 5} more` : ''}.
          </Alert>
        )}

        {data && rows.length === 0 && !loading && (
          <Box sx={{ p: 2, borderRadius: 2, bgcolor: TONE.success.bg, color: TONE.success.fg, fontSize: 14, fontWeight: 600 }}>
            No courier has PoDs pending past the on-time window. Nothing to send.
          </Box>
        )}

        {rows.length > 0 && (
          <>
            <TableContainer sx={{ maxHeight: embedded ? 'calc(100vh - 420px)' : 360, minHeight: 200 }}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding='checkbox' sx={tableHeadSx}>
                      <Checkbox size='small' checked={allOn} indeterminate={!allOn && selected.length > 0} disabled={!mailable.length}
                        onChange={() => setSelected(allOn ? [] : mailable.map((r) => r.courierId))} inputProps={{ 'aria-label': 'Select all couriers with an email' }} />
                    </TableCell>
                    {['Courier', 'Sent to', 'PoD pending', 'Oldest', 'Last reminder'].map((h, i) => (
                      <TableCell key={h} align={i === 2 || i === 3 ? 'right' : 'left'} sx={{ ...tableHeadSx, whiteSpace: 'nowrap' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => {
                    const can = r.emails?.length > 0
                    return (
                      <TableRow key={r.courierId} hover selected={selected.includes(r.courierId)}>
                        <TableCell padding='checkbox'>
                          <Tooltip title={can ? '' : 'No email on the courier master'}>
                            <span><Checkbox size='small' checked={selected.includes(r.courierId)} disabled={!can} onChange={() => toggle(r.courierId)}
                              inputProps={{ 'aria-label': `Select ${r.courierName}` }} /></span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{r.courierName}</TableCell>
                        <TableCell sx={{ minWidth: 180, maxWidth: 280 }}>
                          {can ? <Typography sx={{ fontSize: 13, overflowWrap: 'break-word' }}>{r.emails.join(', ')}</Typography>
                            : <Chip size='small' label='No email' sx={toneChipSx('orange')} />}
                        </TableCell>
                        <TableCell align='right' sx={{ fontWeight: 700, color: 'error.main' }}>{num(r.pending)}</TableCell>
                        <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>{r.oldestDays} {r.oldestDays === 1 ? 'day' : 'days'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', color: r.lastSentAt ? 'text.primary' : 'text.secondary' }}>
                          {r.lastSentAt ? `${fmtDateTime(String(r.lastSentAt).replace(' ', 'T'))} · ${r.lastTrigger === 'AUTO' ? 'daily' : `by ${r.lastSentBy}`}` : 'Never'}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
              <Button variant='outlined' disabled={!selected.length} onClick={() => openTest(selected)} sx={{ textTransform: 'none' }}>
                Send test
              </Button>
              <Button variant='contained' startIcon={<SendOutlinedIcon />} disabled={!selected.length}
                onClick={() => setConfirm({ test: false, ids: selected })} sx={{ textTransform: 'none' }}>
                Send now ({selected.length})
              </Button>
            </Box>
          </>
        )}
      </CardContent>

      {/* confirm send */}
      <Dialog open={!!confirm} onClose={() => !sending && setConfirm(null)} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{confirm?.test ? 'Send a test copy?' : `Email ${confirmRows.length} ${confirmRows.length === 1 ? 'courier' : 'couriers'} now?`}</DialogTitle>
        <DialogContent dividers>
          {confirm?.test && (
            <TextField size='small' fullWidth label='Send test to' value={testTo} onChange={(e) => setTestTo(e.target.value)} sx={{ mb: 1.5 }}
              error={testTo !== '' && !testToOk} helperText={testTo !== '' && !testToOk ? 'Enter an email address' : 'A Proship user or company address; outside addresses are refused.'} />
          )}
          {confirm?.test || data?.testOnly
            ? <Typography sx={{ fontSize: 14, mb: 1.5 }}>One email per courier goes <b>only to {confirm?.test ? (testTo.trim() || 'the address above') : 'you'}</b>, exactly as the courier would get it. Nothing goes to the couriers.</Typography>
            : <Typography sx={{ fontSize: 14, mb: 1.5 }}>Each courier gets its own email with its pending shipments attached{data?.cc?.length ? <>, copied to <b>{data.cc.join(', ')}</b></> : null}.</Typography>}
          <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
            {confirmRows.map((r) => (
              <li key={r.courierId}><Typography sx={{ fontSize: 13 }}><b>{r.courierName}</b>: {num(r.pending)} shipments → {r.emails?.join(', ') || 'no email'}</Typography></li>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setConfirm(null)} disabled={sending} sx={{ textTransform: 'none' }}>Cancel</Button>
          <LoadingButton variant='contained' loading={sending} onClick={send} disabled={confirm?.test && !testToOk} sx={{ textTransform: 'none' }}>
            {confirm?.test ? 'Send test' : 'Send emails'}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* result */}
      <Dialog open={!!result} onClose={() => setResult(null)} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {result ? `${result.sent} sent${result.skipped ? ` · ${result.skipped} skipped` : ''}${result.failed ? ` · ${result.failed} failed` : ''}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {(result?.items || []).map((i) => (
            <Box key={i.courierId} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', py: 0.75 }}>
              <Chip size='small' label={STATUS[i.status]?.label || i.status} sx={{ ...toneChipSx(STATUS[i.status]?.tone || 'neutral'), minWidth: 70 }} />
              <Typography sx={{ fontSize: 13 }}>
                <b>{i.courierName}</b>{i.awbCount != null ? `: ${num(i.awbCount)} shipments` : ''}
                {i.to?.length ? ` → ${i.to.join(', ')}` : ''}{i.reason ? `. ${i.reason}` : ''}
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}><Button variant='contained' onClick={() => setResult(null)} sx={{ textTransform: 'none' }}>Done</Button></DialogActions>
      </Dialog>

      {/* confirm switching off */}
      <Dialog open={offOpen} onClose={() => !switching && setOffOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Switch off the daily courier email?</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 14, mb: 1.5 }}>The 10:00 email stops until someone switches it back on. You can still send reminders from here with Send now.</Typography>
          <TextField size='small' fullWidth label='Reason (optional)' value={offReason} onChange={(e) => setOffReason(e.target.value)}
            placeholder='e.g. courier contacts being updated' />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setOffOpen(false)} disabled={switching} sx={{ textTransform: 'none' }}>Keep it on</Button>
          <LoadingButton color='error' variant='contained' loading={switching} onClick={() => setAuto(false, offReason)} sx={{ textTransform: 'none' }}>Switch off</LoadingButton>
        </DialogActions>
      </Dialog>
    </Card>
  )
}

export default forwardRef(PodRemindersCard)
